import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  PaymentState,
  PaymentPlan,
  BillingInterval,
  PaymentFormData,
  PaymentError,
  CreatePaymentIntentResponse,
} from '../types/payment';
import type { NavigateFunction } from 'react-router-dom';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import type { BackendPlanDescriptor } from '../types/payment';
import { paymentApi } from '../lib/services/payment/paymentApi';

interface PaymentStore extends PaymentState {
  // Price Plan v2 — cohort-aware visible plans (from backend)
  visiblePlans: BackendPlanDescriptor[] | null;
  cohort: 'LEGACY' | 'NEW' | null;
  cohortLockedAt: string | null;
  eoyAt: string | null;
  fetchVisiblePlans: (userId: string) => Promise<void>;

  // Actions
  setSelectedPlan: (plan: PaymentPlan) => void;
  setBillingInterval: (interval: BillingInterval) => void;
  setFormData: (data: Partial<PaymentFormData>) => void;
  setStripe: (stripe: Stripe | null) => void;
  setElements: (elements: StripeElements | null) => void;
  setLoading: (loading: boolean) => void;
  setProcessing: (processing: boolean) => void;
  setError: (error: PaymentError | null) => void;
  setPaymentIntent: (paymentIntent: CreatePaymentIntentResponse | null) => void;
  setPaymentConfirmed: (confirmed: boolean) => void;
  clearError: () => void;
  reset: () => void;

  // Navigation
  navigateTo: (navigate: NavigateFunction) => void;

  // Computed values
  getCurrentPrice: () => number;
  getSelectedPriceId: () => string | null;
  getAnnualSavings: () => number;
  isFormValid: () => boolean;
}

const initialState: PaymentState = {
  selectedPlan: null,
  billingInterval: 'monthly',
  formData: {},
  stripe: null,
  elements: null,
  isLoading: false,
  isProcessing: false,
  error: null,
  paymentIntent: null,
  paymentConfirmed: false,
};

export const usePaymentStore = create<PaymentStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // Price Plan v2 state
    visiblePlans: null,
    cohort: null,
    cohortLockedAt: null,
    eoyAt: null,

    fetchVisiblePlans: async (userId: string) => {
      try {
        const resp = await paymentApi.getVisiblePlans(userId);
        set({
          visiblePlans: resp.plans,
          cohort: resp.cohort,
          cohortLockedAt: resp.cohortLockedAt,
          eoyAt: resp.eoyAt,
        });
      } catch (e) {
        // Non-fatal: pages fall back to the bundled UNIFIED_PLANS when null.
        console.error('fetchVisiblePlans failed', e);
      }
    },

    // Actions
    setSelectedPlan: plan => set({ selectedPlan: plan, error: null }),

    setBillingInterval: interval => set({ billingInterval: interval, error: null }),

    setFormData: data =>
      set(state => ({
        formData: { ...state.formData, ...data },
        error: null,
      })),

    setStripe: stripe => set({ stripe }),

    setElements: elements => set({ elements }),

    setLoading: loading => set({ isLoading: loading }),

    setProcessing: processing => set({ isProcessing: processing }),

    setError: error => set({ error, isLoading: false, isProcessing: false }),

    setPaymentIntent: paymentIntent => set({ paymentIntent }),

    setPaymentConfirmed: confirmed =>
      set({
        paymentConfirmed: confirmed,
        isProcessing: false,
        isLoading: false,
      }),

    clearError: () => set({ error: null }),

    reset: () => set(initialState),

    navigateTo: (navigate: NavigateFunction) => {
      // Land in the app on Home (the default dashboard)
      navigate('/home');
    },

    // Computed values
    getCurrentPrice: () => {
      const { selectedPlan, billingInterval } = get();
      if (!selectedPlan) return 0;
      return billingInterval === 'annual' ? selectedPlan.yearlyPrice : selectedPlan.monthlyPrice;
    },

    getSelectedPriceId: () => {
      const { selectedPlan, billingInterval } = get();
      if (!selectedPlan) return null;

      return `price_${selectedPlan.id}_${billingInterval}`;
    },

    getAnnualSavings: () => {
      const { selectedPlan } = get();
      if (!selectedPlan) return 0;

      const monthlyTotal = selectedPlan.monthlyPrice * 12;
      const annualPrice = selectedPlan.yearlyPrice * 12;
      return monthlyTotal - annualPrice;
    },

    isFormValid: () => {
      const { formData } = get();
      return !!(
        formData.email &&
        formData.cardholderName &&
        formData.billingAddress?.country &&
        formData.billingAddress?.address
      );
    },
  }))
);

// Selectors for common use cases
export const useSelectedPlan = () => usePaymentStore(state => state.selectedPlan);
export const useBillingInterval = () => usePaymentStore(state => state.billingInterval);
export const useCurrentPrice = () => usePaymentStore(state => state.getCurrentPrice());

// Subscribe to payment state changes
usePaymentStore.subscribe(
  state => state.selectedPlan,
  selectedPlan => {
    if (selectedPlan) {
      console.log('Plan selected:', selectedPlan.name);
    }
  }
);

usePaymentStore.subscribe(
  state => state.error,
  error => {
    if (error) {
      console.error('Payment error:', error);
    }
  }
);
